/**
 * 의약품 수기 대량 업로드 다이얼로그.
 */
import { useInsertDrugsManual } from '@/entities/drug-data/hooks/useDrugData';
import {
  chunkArray,
  downloadDrugBulkUploadTemplate,
  parseDrugBulkUploadFile,
} from '@/pages/drugs/drugBulkUploadUtils';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Download } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';

const CHUNK_SIZE = 100;

interface DrugBulkUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultRefYm?: string;
  onSuccess?: () => void;
}

export const DrugBulkUploadDialog: React.FC<DrugBulkUploadDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const insertDrugs = useInsertDrugsManual();
  const [fileName, setFileName] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const reset = () => {
    setFileName('');
    setRowCount(0);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const items = await parseDrugBulkUploadFile(file);
      if (items.length === 0) {
        toast.error('업로드할 데이터 행이 없습니다.');
        return;
      }
      const invalid = items.find((item) => !item.refYm || !item.drugCode || !item.drugName);
      if (invalid) {
        toast.error('기준년월, 의약품 코드, 의약품명은 필수입니다.');
        return;
      }
      setFileName(file.name);
      setRowCount(items.length);
      setIsUploading(true);
      const chunks = chunkArray(items, CHUNK_SIZE);
      for (const chunk of chunks) {
        await insertDrugs.mutateAsync(chunk);
      }
      toast.success(`${items.length}건의 의약품을 등록했습니다.`);
      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isUploading) {
          onOpenChange(next);
          if (!next) reset();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>의약품 대량 업로드</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          XLSX 양식에 맞춰 수기 의약품을 일괄 등록합니다. (최대 {CHUNK_SIZE}건씩 전송)
        </p>
        <ToolbarRow gap={8}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void downloadDrugBulkUploadTemplate()}
          >
            <Download className="mr-2 h-4 w-4" />
            양식 다운로드
          </Button>
          <FileUploadButton
            inputRef={inputRef}
            accept=".xlsx,.xls"
            disabled={isUploading}
            resetOnSelect={false}
            onChange={handleFileChange}
          >
            {isUploading ? '업로드 중...' : '파일 선택'}
          </FileUploadButton>
        </ToolbarRow>
        {fileName ? (
          <Label className="text-xs text-muted-foreground">
            선택: {fileName} ({rowCount}행 처리됨)
          </Label>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
